<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 md:p-4">
    <div class="bg-white shadow-xl w-full md:max-w-md md:rounded-lg md:h-auto h-full max-h-screen overflow-hidden flex flex-col rounded-none">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
        <h2 class="text-xl font-semibold text-gray-800">انتخاب روش پرداخت</h2>
        <button
          @click="$emit('close')"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="p-4 md:p-6 flex-1 overflow-y-auto">
        <div class="mb-6">
          <p class="text-gray-600 mb-4">لطفاً روش پرداخت سفارش #{{ orderId }} را انتخاب کنید:</p>
          <p class="text-sm text-gray-500 mb-4">مبلغ کل: {{ formatPrice(totalAmount) }}</p>
        </div>

        <!-- Payment Method Selection -->
        <div class="space-y-3 mb-6">
          <label class="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              v-model="selectedMethod"
              value="cash"
              class="ml-3 text-brown-600 focus:ring-brown-500"
            />
            <div>
              <div class="font-medium text-gray-800">نقد</div>
              <div class="text-sm text-gray-500">پرداخت نقدی</div>
            </div>
          </label>

          <label class="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              v-model="selectedMethod"
              value="pos"
              class="ml-3 text-brown-600 focus:ring-brown-500"
            />
            <div>
              <div class="font-medium text-gray-800">POS</div>
              <div class="text-sm text-gray-500">پرداخت با کارت خوان</div>
            </div>
          </label>

          <label class="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              v-model="selectedMethod"
              value="card"
              class="ml-3 text-brown-600 focus:ring-brown-500"
            />
            <div>
              <div class="font-medium text-gray-800">کارت به کارت</div>
              <div class="text-sm text-gray-500">انتقال کارت به کارت</div>
            </div>
          </label>

          <label class="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              v-model="selectedMethod"
              value="mixed"
              class="ml-3 text-brown-600 focus:ring-brown-500"
            />
            <div>
              <div class="font-medium text-gray-800">ترکیبی</div>
              <div class="text-sm text-gray-500">ترکیبی از روش‌های مختلف</div>
            </div>
          </label>
        </div>

        <!-- Mixed Payment Details -->
        <div v-if="selectedMethod === 'mixed'" class="space-y-4 mb-6">
          <div class="bg-gray-50 p-4 rounded-lg">
            <h3 class="font-medium text-gray-800 mb-3">جزئیات پرداخت ترکیبی</h3>
            
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">مبلغ نقدی</label>
                <input
                  v-model.number="mixedPayment.cash"
                  type="number"
                  min="0"
                  step="1000"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">مبلغ POS</label>
                <input
                  v-model.number="mixedPayment.pos"
                  type="number"
                  min="0"
                  step="1000"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">مبلغ کارت به کارت</label>
                <input
                  v-model.number="mixedPayment.card"
                  type="number"
                  min="0"
                  step="1000"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>
            </div>

            <!-- Total Validation -->
            <div class="mt-3 p-2 rounded text-sm" :class="getMixedTotalClass()">
              <div class="flex justify-between">
                <span>مجموع:</span>
                <span>{{ formatPrice(getMixedTotal()) }}</span>
              </div>
              <div class="flex justify-between">
                <span>مبلغ کل:</span>
                <span>{{ formatPrice(totalAmount) }}</span>
              </div>
              <div class="flex justify-between font-medium">
                <span>تفاوت:</span>
                <span>{{ formatPrice(getMixedTotal() - totalAmount) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-red-600 text-sm">{{ error }}</p>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end space-x-3 space-x-reverse p-4 md:p-6 border-t border-gray-200 sticky bottom-0 bg-white">
        <button
          @click="$emit('close')"
          class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          انصراف
        </button>
        <button
          @click="confirmPayment"
          :disabled="!canConfirm"
          class="px-6 py-2 bg-brown-600 text-white rounded-lg font-medium hover:bg-brown-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          تایید و بستن سفارش
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'

export default {
  name: 'PaymentMethodModal',
  props: {
    orderId: {
      type: Number,
      required: true
    },
    totalAmount: {
      type: Number,
      required: true
    }
  },
  emits: ['close', 'confirm'],
  setup(props, { emit }) {
    const selectedMethod = ref('pos')
    const error = ref('')
    const mixedPayment = ref({
      cash: 0,
      pos: 0,
      card: 0
    })

    const formatPrice = (price) => {
      return new Intl.NumberFormat('fa-IR').format(price || 0) + ' تومان'
    }

    const getMixedTotal = () => {
      return mixedPayment.value.cash + mixedPayment.value.pos + mixedPayment.value.card
    }

    const getMixedTotalClass = () => {
      const total = getMixedTotal()
      if (total === props.totalAmount) {
        return 'bg-green-50 text-green-700'
      } else if (total > props.totalAmount) {
        return 'bg-yellow-50 text-yellow-700'
      } else {
        return 'bg-red-50 text-red-700'
      }
    }

    const canConfirm = computed(() => {
      if (!selectedMethod.value) return false
      
      if (selectedMethod.value === 'mixed') {
        const total = getMixedTotal()
        return total === props.totalAmount
      }
      
      return true
    })

    const confirmPayment = () => {
      if (!canConfirm.value) {
        error.value = 'لطفاً اطلاعات پرداخت را به درستی وارد کنید'
        return
      }

      let paymentData = {
        method: selectedMethod.value
      }

      if (selectedMethod.value === 'mixed') {
        paymentData.mixedPayment = {
          cash: mixedPayment.value.cash,
          pos: mixedPayment.value.pos,
          card: mixedPayment.value.card
        }
      }

      emit('confirm', paymentData)
    }

    return {
      selectedMethod,
      error,
      mixedPayment,
      formatPrice,
      getMixedTotal,
      getMixedTotalClass,
      canConfirm,
      confirmPayment
    }
  }
}
</script>

<style scoped>
/* Custom styles if needed */
</style>
